import html2pdf from "html2pdf.js";
import React, { useRef } from "react";
import { Property } from "../models/property.model";
import "./ProposalPreview.css";

interface ProposalPreviewProps {
  // Proposal basic info
  proposalName: string;
  proposalDescription?: string;
  referenceCode?: string;
  startDate: Date | null;
  endDate: Date | null;
  currencySymbol?: string;
  isShowPrice?: boolean;

  // RMC and Client info
  rmcName?: string;
  clientName?: string;

  // Properties and rooms
  selectedProperties: Property[];
  propertyRoomData: Record<
    number,
    {
      propertyId: number;
      property: Property;
      selectedRooms: number[];
      roomCharges?: Record<number, { amount: number; taxPercentage?: number }>;
      roomComplementaries?: Record<number, number[]>;
      isRMCClient: boolean;
    }
  >;

  // Terms
  propertyTerms: Record<number, string>;

  // City names mapping (cityId -> cityName)
  cityNames?: Record<number, string>;

  // Viewing mode
  viewMode?: boolean;
}

const ProposalPreview: React.FC<ProposalPreviewProps> = ({
  proposalName,
  proposalDescription,
  referenceCode,
  startDate,
  endDate,
  currencySymbol = "$",
  isShowPrice = true,
  rmcName,
  clientName,
  selectedProperties,
  propertyRoomData,
  propertyTerms,
  cityNames = {},
  viewMode = false,
}) => {
  const documentRef = useRef<HTMLDivElement>(null);

  const formatDate = (date: Date | null): string => {
    if (!date) return "N/A";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number): string => {
    return `${currencySymbol}${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Helper function to get city name
  const getCityName = (property: Property): string => {
    // Try multiple ways to get city name
    if (property.city?.name) return property.city.name;
    if (property.cityId && cityNames[property.cityId])
      return cityNames[property.cityId];
    // Fallback: try to get from propertyRoomData
    const roomData = propertyRoomData[property.id!];
    if (roomData?.property?.city?.name) return roomData.property.city.name;
    return "City Not Specified";
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = () => {
    if (!documentRef.current) return;

    const element = documentRef.current;
    const opt = {
      margin: [10, 10, 10, 10] as [number, number, number, number],
      filename: `Proposal_${proposalName.replace(/\s+/g, "_")}_${
        new Date().toISOString().split("T")[0]
      }.pdf`,
      image: { type: "jpeg" as const, quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: {
        unit: "mm" as const,
        format: "a4" as const,
        orientation: "portrait" as const,
      },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    html2pdf().set(opt).from(element).save();
  };

  const calculatePropertyTotal = (propertyId: number): number => {
    const roomData = propertyRoomData[propertyId];
    if (!roomData || !roomData.roomCharges) return 0;

    return Object.values(roomData.roomCharges).reduce((sum, charge) => {
      return sum + charge.amount;
    }, 0);
  };

  const calculateGrandTotal = (): number => {
    return selectedProperties.reduce((total, property) => {
      return total + calculatePropertyTotal(property.id!);
    }, 0);
  };

  const getRoomName = (roomId: number, property: Property): string => {
    // RoomTypeDto only contains id, name, description
    const roomType = property.roomTypes?.find((r) => r.id === roomId);
    return roomType?.name || `Room ${roomId}`;
  };

  const getRoomDescription = (
    roomId: number,
    property: Property
  ): string | undefined => {
    const roomType = property.roomTypes?.find((r) => r.id === roomId);
    return roomType?.description;
  };

  const getComplementaryCount = (complementaryIds: number[]): string => {
    if (!complementaryIds || complementaryIds.length === 0) return "None";
    return `${complementaryIds.length} item${
      complementaryIds.length > 1 ? "s" : ""
    }`;
  };

  const defaultTerms = [
    "This proposal is valid for the period mentioned above and subject to property availability.",
    "Rates are subject to change without prior notice if booked after the valid period.",
    "All payments must be made as per the agreed credit terms.",
    "Cancellation policy applies as per standard corporate housing agreement.",
    "Any damage to the property will be charged separately and is not included in the quoted rates.",
    "Check-in and check-out times are as per property standard policies.",
    "Additional services requested beyond the standard package will be charged separately.",
    "The rates quoted are exclusive of any applicable taxes unless otherwise stated.",
  ];

  return (
    <div className="proposal-preview">
      <div className="print-actions">
        <button className="print-button" onClick={handlePrint}>
          <i className="pi pi-print"></i>
          Print
        </button>
        <button className="export-button" onClick={handleExportPDF}>
          <i className="pi pi-file-pdf"></i>
          Export PDF
        </button>
      </div>

      <div className="proposal-document" ref={documentRef}>
        <div className="document-page">
          {/* Header */}
          <div className="document-header">
            <div className="company-logo-section">
              <h1 className="company-name">IOS RELOCATIONS</h1>
              <p className="company-tagline">
                Your Trusted Partner in Corporate Housing Solutions
              </p>
            </div>
            <h2 className="document-title">HOUSING PROPOSAL</h2>
          </div>

          {/* Compact Proposal Information - Two Column Layout */}
          <div className="proposal-info-compact">
            <div className="info-row">
              <div className="info-item-inline">
                <span className="info-label">PROPOSAL NAME</span>
                <span className="info-value">{proposalName}</span>
              </div>
              <div className="info-item-inline">
                <span className="info-label">RMC COMPANY</span>
                <span className="info-value">{rmcName || "N/A"}</span>
              </div>
            </div>
            <div className="info-row">
              <div className="info-item-inline">
                <span className="info-label">CLIENT NAME</span>
                <span className="info-value">{clientName || "N/A"}</span>
              </div>
              <div className="info-item-inline">
                <span className="info-label">PROPOSAL DATE</span>
                <span className="info-value">{formatDate(new Date())}</span>
              </div>
            </div>
            <div className="info-row">
              <div className="info-item-inline">
                <span className="info-label">VALID FROM</span>
                <span className="info-value">{formatDate(startDate)}</span>
              </div>
              <div className="info-item-inline">
                <span className="info-label">VALID UNTIL</span>
                <span className="info-value">{formatDate(endDate)}</span>
              </div>
            </div>
          </div>

          {proposalDescription && (
            <div className="document-section">
              <div className="section-title">Description</div>
              <div className="terms-content">
                <p>{proposalDescription}</p>
              </div>
            </div>
          )}

          {/* Properties and Rooms */}
          <div className="document-section">
            <div className="section-title">
              Proposed Properties & Accommodations
            </div>

            {selectedProperties.length === 0 ? (
              <div className="empty-state">
                <i className="pi pi-home"></i>
                <p>No properties selected</p>
              </div>
            ) : (
              selectedProperties.map((property) => {
                const roomData = propertyRoomData[property.id!];
                if (!roomData) return null;

                return (
                  <div key={property.id} className="property-card">
                    <div className="property-header">
                      <div>
                        <h3 className="property-name">{property.name}</h3>
                        <div className="property-meta">
                          <p className="property-city">
                            <i className="pi pi-map-marker"></i>
                            {getCityName(property)}
                          </p>
                          {property.location?.name && (
                            <p className="property-location">
                              <i className="pi pi-building"></i>
                              Location: {property.location.name}
                            </p>
                          )}
                          {property.location?.zone?.name && (
                            <p className="property-zone">
                              <i className="pi pi-globe"></i>
                              Zone: {property.location.zone.name}
                            </p>
                          )}
                        </div>
                        {property.address && (
                          <p className="property-address">
                            <i className="pi pi-home"></i>
                            {property.address}
                          </p>
                        )}
                      </div>
                      <div className="property-type-badge">
                        {property.propertyType?.name || "N/A"}
                      </div>
                    </div>

                    {/* Property Details Section */}
                    {(property.description ||
                      property.zipCode ||
                      property.landmark ||
                      property.furnishingType ||
                      property.buildYear ||
                      property.totalInventory ||
                      //property.creditPeriod ||
                      property.website ||
                      property.brochureLink ||
                      property.videoLink ||
                      property.masterPlanLink ||
                      property.additionalComment) && (
                      <div className="property-details-section">
                        {property.description && (
                          <div className="detail-item">
                            <span className="detail-label">
                              <i className="pi pi-info-circle"></i> Description:
                            </span>
                            <span className="detail-value">
                              {property.description}
                            </span>
                          </div>
                        )}

                        <div className="detail-row">
                          {property.zipCode && (
                            <div className="detail-item-inline">
                              <span className="detail-label">
                                <i className="pi pi-map"></i> Zip Code:
                              </span>
                              <span className="detail-value">
                                {property.zipCode}
                              </span>
                            </div>
                          )}

                          {property.landmark && (
                            <div className="detail-item-inline">
                              <span className="detail-label">
                                <i className="pi pi-compass"></i> Landmark:
                              </span>
                              <span className="detail-value">
                                {property.landmark}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="detail-row">
                          {property.furnishingType && (
                            <div className="detail-item-inline">
                              <span className="detail-label">
                                <i className="pi pi-box"></i> Furnishing:
                              </span>
                              <span className="detail-value">
                                {property.furnishingType}
                              </span>
                            </div>
                          )}

                          {property.buildYear && (
                            <div className="detail-item-inline">
                              <span className="detail-label">
                                <i className="pi pi-calendar"></i> Built Year:
                              </span>
                              <span className="detail-value">
                                {property.buildYear}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="detail-row">
                          {property.totalInventory && (
                            <div className="detail-item-inline">
                              <span className="detail-label">
                                <i className="pi pi-database"></i> Total
                                Inventory:
                              </span>
                              <span className="detail-value">
                                {property.totalInventory} units
                              </span>
                            </div>
                          )}

                          {/* {property.creditPeriod !== undefined &&
                            property.creditPeriod > 0 && (
                              <div className="detail-item-inline">
                                <span className="detail-label">
                                  <i className="pi pi-clock"></i> Credit Period:
                                </span>
                                <span className="detail-value">
                                  {property.creditPeriod} days
                                </span>
                              </div>
                            )} */}
                        </div>

                        {property.website && (
                          <div className="detail-item">
                            <span className="detail-label">
                              <i className="pi pi-globe"></i> Website:
                            </span>
                            <span className="detail-value">
                              <a
                                href={property.website}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {property.website}
                              </a>
                            </span>
                          </div>
                        )}

                        {property.brochureLink && (
                          <div className="detail-item">
                            <span className="detail-label">
                              <i className="pi pi-file-pdf"></i> Brochure:
                            </span>
                            <span className="detail-value">
                              <a
                                href={property.brochureLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View Brochure
                              </a>
                            </span>
                          </div>
                        )}

                        {property.videoLink && (
                          <div className="detail-item">
                            <span className="detail-label">
                              <i className="pi pi-video"></i> Video:
                            </span>
                            <span className="detail-value">
                              <a
                                href={property.videoLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Watch Video
                              </a>
                            </span>
                          </div>
                        )}

                        {property.masterPlanLink && (
                          <div className="detail-item">
                            <span className="detail-label">
                              <i className="pi pi-sitemap"></i> Master Plan:
                            </span>
                            <span className="detail-value">
                              <a
                                href={property.masterPlanLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View Master Plan
                              </a>
                            </span>
                          </div>
                        )}

                        {property.additionalComment && (
                          <div className="detail-item">
                            <span className="detail-label">
                              <i className="pi pi-comment"></i> Additional
                              Comments:
                            </span>
                            <span className="detail-value">
                              {property.additionalComment}
                            </span>
                          </div>
                        )}

                        {property.amenities &&
                          property.amenities.length > 0 && (
                            <div className="detail-item">
                              <span className="detail-label">
                                <i className="pi pi-star"></i> Amenities:
                              </span>
                              <span className="detail-value">
                                {property.amenities
                                  .map((a) => a.name)
                                  .join(", ")}
                              </span>
                            </div>
                          )}

                        {property.facilities &&
                          property.facilities.length > 0 && (
                            <div className="detail-item">
                              <span className="detail-label">
                                <i className="pi pi-wrench"></i> Facilities:
                              </span>
                              <span className="detail-value">
                                {property.facilities
                                  .map((f) => f.name)
                                  .join(", ")}
                              </span>
                            </div>
                          )}
                      </div>
                    )}

                    {roomData.selectedRooms &&
                    roomData.selectedRooms.length > 0 ? (
                      <>
                        <table className="rooms-table">
                          <thead>
                            <tr>
                              <th>Room Type & Description</th>
                              {isShowPrice && <th>Charges</th>}
                              <th>Complementaries</th>
                            </tr>
                          </thead>
                          <tbody>
                            {roomData.selectedRooms.map((roomId) => {
                              const charge = roomData.roomCharges?.[roomId];
                              const complementaries =
                                roomData.roomComplementaries?.[roomId] || [];
                              const roomDescription = getRoomDescription(
                                roomId,
                                property
                              );

                              return (
                                <tr key={roomId}>
                                  <td>
                                    <div className="room-name">
                                      {getRoomName(roomId, property)}
                                    </div>
                                    {roomDescription && (
                                      <div className="room-description">
                                        {roomDescription}
                                      </div>
                                    )}
                                  </td>
                                  {isShowPrice && (
                                    <td className="price-cell">
                                      {charge?.amount ? (
                                        <div>
                                          <div className="charge-amount">
                                            {formatCurrency(charge.amount)}
                                          </div>
                                          {charge?.taxPercentage && (
                                            <div className="charge-tax">
                                              Tax: {charge.taxPercentage}%
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        "-"
                                      )}
                                    </td>
                                  )}
                                  <td>
                                    <div className="complementaries-list">
                                      {complementaries.length > 0
                                        ? `${
                                            complementaries.length
                                          } complementar${
                                            complementaries.length === 1
                                              ? "y"
                                              : "ies"
                                          } included`
                                        : "None"}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {/* Property-specific terms */}
                        {propertyTerms[property.id!] && (
                          <div className="property-terms">
                            <div className="property-terms-title">
                              <i className="pi pi-info-circle"></i> Additional
                              Terms for this Property
                            </div>
                            <div
                              className="property-terms-content"
                              dangerouslySetInnerHTML={{
                                __html: propertyTerms[property.id!],
                              }}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="empty-state">
                        <p>No rooms selected for this property</p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Terms and Conditions */}
          <div className="document-section">
            <div className="section-title">Terms & Conditions</div>
            <div className="terms-content">
              <ol className="terms-list">
                {defaultTerms.map((term, index) => (
                  <li key={index}>{term}</li>
                ))}
              </ol>
            </div>
          </div>

          {/* Signature Section */}
          <div className="signature-section">
            <div className="signature-grid">
              <div className="signature-box">
                <div className="signature-line">
                  <div className="signature-label">For IOS Relocations</div>
                  <div className="signature-name">Authorized Signatory</div>
                  <div className="signature-date">Date: ______________</div>
                </div>
              </div>
              <div className="signature-box">
                <div className="signature-line">
                  <div className="signature-label">
                    For {rmcName || "Client Company"}
                  </div>
                  <div className="signature-name">Authorized Signatory</div>
                  <div className="signature-date">Date: ______________</div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="document-footer">
            <div className="footer-contact">
              <strong>IOS Relocations</strong> | Email: info@iosrelocations.com
              | Phone: +1 (XXX) XXX-XXXX
            </div>
            <div className="footer-note">
              This is a system-generated proposal document. For any queries,
              please contact our team.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalPreview;
